@if ( !isSystemForm) {
  <div class="flex flex-wrap md:flex-nowrap justify-between items-center max-w-4xl mx-auto pb-2" [class.preview-chrome--compact]="collapsibleSections">
    <!-- Status -->
    <div class="flex justify-center items-center rounded-[30px] w-[120px] h-[26px] text-[12px] order-2 md:order-1" [class]="completedOn?? '' !== '' ? 'bg-[#40b6d7] text-white' : 'bg-[var(--status-active-bg)] text-[var(--status-active-fg)]'">
      <span>{{ (completedOn?? '') !== '' ? "Completed" : "In Progress" }}</span>
    </div>
    <!-- Note Caption -->
    <div class="text-gray-800 text-xs flex flex-col w-full order-1 md:order-2 pb-2 md:pb-0 items-center text-center md:flex-1">
      @if (!readOnly) {
        <span><strong>Note:</strong> Your changes are automatically saved while filling the form</span>
      }
      <div [innerHTML]="lastEditedInfo" class="flex justify-center"></div>
    </div>
    <!-- Actions -->
    <div class="flex items-center gap-3 text-sm pr-1 text-[#474554] order-3 md:order-3 ml-auto">
      @if (linkSummary) {
        <button type="button"
          class="flex items-center gap-2 text-xs font-medium px-3 py-1 rounded-full border transition hover:shadow-sm disabled:opacity-60 disabled:cursor-not-allowed"
          [ngClass]="(linkSummary?.count ?? 0) > 0 ? 'text-slate-600 border-slate-200 bg-slate-50 hover:bg-slate-100' : 'text-primary border-slate-200 hover:border-slate-300 hover:text-slate-600'"
          [disabled]="linkSummary?.loading || linkSummary?.disabled"
          [attr.title]="linkSummary?.disabled ? 'Save inspection to enable linking' : null"
          (click)="onLinkSummaryAction()">
          <i class="fas fa-link text-[12px]"></i>
          @if (linkSummary?.loading) {
            <span>Loading...</span>
          } @else {
            @if ((linkSummary?.count ?? 0) > 0) {
              <span>
                Associated with {{ linkSummary?.count }} {{ linkSummary?.label ?? 'records' }}
              </span>
            }
            @if ((linkSummary?.count ?? 0) === 0) {
              <span>
                Add Link
              </span>
            }
          }
        </button>
      }
      @if (!readOnly) {
        <button matButton [matMenuTriggerFor]="tmpPrvMenu" class="text-xs"><i class="fas fa-cog"></i> Settings</button>
        <mat-menu #tmpPrvMenu="matMenu">
          <button mat-menu-item (click)="formatTitle()">Format Title</button>
        </mat-menu>
      }
    </div>
  </div>
}

@if (!isSystemForm && collapsibleSections) {
  <!-- The inspection panel: the template's name, and — only when there is more than one page — a page
       navigator (previous / next, and a numbered list), instead of a picker with nothing to pick. -->
  <div class="preview-pages">
    <div class="preview-pages__template">
      <mat-icon aria-hidden="true" svgIcon="c:file-text"></mat-icon>
      <span class="preview-pages__name">{{ templateName }}</span>
      @if (enableScoring) {
        <span class="preview-pages__score">
          {{ score ?? 0 }} / {{ totalScore ?? 0 }} ({{ (((score ?? 0) / (totalScore || 1)) * 100).toFixed(0) }}%)
        </span>
      }
    </div>
    @if ((templateData?.length ?? 0) > 1) {
      <div class="preview-pages__nav">
        <button type="button" mat-icon-button aria-label="Previous page" [disabled]="currentPageIndex === 0" (click)="goToPage(currentPageIndex - 1)">
          <mat-icon svgIcon="c:chevron-left"></mat-icon>
        </button>
        <button type="button" class="preview-pages__current" [matMenuTriggerFor]="pageMenu" aria-label="Choose a page">
          <span class="preview-pages__count">Page {{ currentPageIndex + 1 }} of {{ templateData.length }}</span>
          <span class="preview-pages__caption">{{ pageLabel(templateData[currentPageIndex], currentPageIndex) }}</span>
          <mat-icon aria-hidden="true" svgIcon="c:chevron-down"></mat-icon>
        </button>
        <button type="button" mat-icon-button aria-label="Next page" [disabled]="currentPageIndex >= templateData.length - 1" (click)="goToPage(currentPageIndex + 1)">
          <mat-icon svgIcon="c:chevron-right"></mat-icon>
        </button>
        <mat-menu #pageMenu="matMenu" class="preview-pages-menu">
          @for (page of templateData; track page; let i = $index) {
            <button type="button" mat-menu-item [class.is-current]="i === currentPageIndex" (click)="goToPage(i)">
              <span class="preview-pages-menu__number">{{ i + 1 }}</span>
              <span>{{ pageLabel(page, i) }}</span>
              @if (i === currentPageIndex) {
                <mat-icon class="preview-pages-menu__check" svgIcon="c:check"></mat-icon>
              }
            </button>
          }
        </mat-menu>
      </div>
    }
  </div>
}

@if (!isSystemForm && !collapsibleSections) {
  <div class="page-selector max-w-4xl mx-auto border rounded-md shadow bg-[var(--color-hover-bg)]">
    <!-- Header -->
    <div class="flex items-center justify-between px-4 py-2 border-b text-sm text-[#474554]">
      <span>Page {{(stepper?.selectedIndex ?? 0) + 1}} of {{templateData?.length}}</span>
      <h2 class="text-sm">{{templateName}}</h2>
      <div>
        @if (enableScoring) {
          <span class="flex items-center gap-1">
            {{ score ?? 0 }} / {{ totalScore ?? 0 }} ({{ (((score ?? 0) / (totalScore || 1)) * 100).toFixed(2) }}%)
          </span>
        }
      </div>
    </div>
    <!-- Clickable row -->
    <div class="p-3 pl-4 cursor-pointer flex justify-between items-center menu-btn" role="button" tabindex="0" (click)="onClickPageNavigation($event)" (keydown.enter)="onClickPageNavigation($event)">
      <p class="text-xs text-[#474554]">{{currentPageCaption}}</p>
      <!-- Arrow -->
      <svg xmlns="http://www.w3.org/2000/svg"
        class="arrowIcon h-5 w-5 text-slate-600 transition-transform duration-200"
        fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </div>
    <!-- Dropdown content -->
    @if (templateData?.length > 0) {
      <div class="menu-item hidden absolute left-0 right-0 mt-1 max-w-4xl mx-auto bg-white border rounded-md shadow-lg z-50"
        >
        @for (pageInfo of templateData; track pageInfo; let i = $index) {
          <div class="p-3 text-xs hover:bg-gray-50 cursor-pointer text-left" role="button" tabindex="0" (click)="onSelectPage(i)" (keydown.enter)="onSelectPage(i)">
            <p class="text-[#474554] font-medium">{{pageInfo.caption}}</p>
          </div>
        }
      </div>
    }
  </div>
}

@if (templateData?.length > 0) {
  <form [formGroup]="templatePreviewForm" class="flex flex-col max-w-4xl mx-auto my-4 gap-5" [class.preview--collapsible]="collapsibleSections">
    @if (collapsibleSections) {
      <!-- The inspection side panel: sections fold (first open per page), and open or close together. -->
      <div class="preview-sections-toolbar">
        <span class="preview-sections-toolbar__label">Sections</span>
        <button type="button" mat-stroked-button class="preview-sections-toolbar__button"
          [disabled]="allSectionsAre(true)" (click)="setAllSectionsExpanded(true)">
          <mat-icon svgIcon="c:chevron-up-down"></mat-icon>Expand all
        </button>
        <button type="button" mat-stroked-button class="preview-sections-toolbar__button"
          [disabled]="allSectionsAre(false)" (click)="setAllSectionsExpanded(false)">
          <mat-icon class="c-icon-slate">unfold_less</mat-icon>Collapse all
        </button>
      </div>
    }
    <mat-stepper orientation="vertical" [linear]="false" #stepper class="preview-stepper">
      @for (pageInfo of templateData; track pageInfo; let i = $index) {
        @if (checkExpression(pageInfo, null)) {
          <mat-step [stepControl]="templatePreviewForm">
            <!-- <ng-template matStepLabel>{{pageInfo.caption}}</ng-template> -->
            @for (sectionInfo of pageInfo.sections; track sectionInfo) {
              @if (checkExpression(sectionInfo, sectionInfo)) {
                <mat-expansion-panel [expanded]="isSectionExpanded(pageInfo, sectionInfo)" [hideToggle]="!collapsibleSections"
                  (opened)="onSectionToggled(sectionInfo, true)" (closed)="onSectionToggled(sectionInfo, false)"
                  class="cus-mat-expansion-panel-primary section">
                  @if ( !isSystemForm) {
                    <mat-expansion-panel-header>
                      <mat-panel-title class="!text-xs">
                        @if (collapsibleSections) {
                          <!-- The panel: a readable name, and how far along the section is. -->
                          @let progress = sectionProgress(sectionInfo);
                          <span class="preview-section-heading">
                            <span class="preview-section-heading__name">{{ sectionLabel(pageInfo, sectionInfo) }}</span>
                            @if (progress.total > 0) {
                              <span class="preview-section-heading__progress">
                                <span [class.is-done]="progress.answered === progress.total">
                                  {{ progress.answered }} of {{ progress.total }} answered
                                </span>
                                @if (progress.missing > 0) {
                                  <span class="preview-section-heading__badge">{{ progress.missing }} required</span>
                                }
                              </span>
                            }
                          </span>
                        } @else {
                          {{sectionInfo.caption + (sectionInfo.allowRepeat ? (" - " + (sectionInfo.sectionNo??1)) : "")}}
                        }
                      </mat-panel-title>
                      <mat-panel-description class="flex flex-end !mr-0" (click)="$event.stopPropagation();" style="flex-grow: unset;">
                        @if (enableScoring) {
                          <span class="flex items-center gap-1 text-xs" style="color: var(--white-color);">
                            {{ getSectionScore(sectionInfo) }}
                          </span>
                        }
                        @if (sectionInfo.sectionNo > 1 && !readOnly) {
                          <button class="btn-action text-white" data-toggle="tooltip" title="Delete Section" (click)="onClickDeleteSection(pageInfo, sectionInfo)">
                            <mat-icon class="c-fa" svgIcon="c:trash"></mat-icon>
                          </button>
                        }
                      </mat-panel-description>
                    </mat-expansion-panel-header>
                  }
                  <div class="mx-auto md:p-4 p-2 mx-auto flex flex-col gap-4">
                    <div class="flex flex-wrap xl:-mx-4 md:-mx-2 xl:-my-3 -my-2 pt-2">
                      @for (itemInfo of sectionInfo.items; track itemInfo) {
                        <!-- Text Field -->
                        @if (itemInfo.fieldType === templateFieldType.Text && checkExpression(itemInfo, sectionInfo)) {
                          <div class="w-full xl:px-4 md:px-2 xl:py-1 pt-2 item-block">
                            <div class="static-label-mat-form-field">
                              <h4 class="text-xs text-left mb-1 font-sans font-medium text-[#474554]">
                                {{ itemInfo.caption }} @if (itemInfo.isMandatory) {
                                <span class="text-red">*</span>
                              }
                            </h4>
                            <mat-form-field appearance="outline" class="w-full cmn-sm-form-field">
                              @if (itemInfo.helpText) {
                                <mat-icon matTooltipClass="cus-primary-tooltip" matTooltip="{{ itemInfo.helpText }}" class="text-primary" matSuffix svgIcon="c:question-circle"></mat-icon>
                              }
                              <input matInput type="text" [formControlName]="itemInfo.formattedItemCode" [(ngModel)]="checklists[itemInfo?.formattedItemCode].val" class="!text-xs" placeholder="Enter Text">
                              @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.touched && templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('required')) {
                                <mat-error class="text-xs">
                                  This field is required
                                </mat-error>
                              }
                            </mat-form-field>
                            <ng-container [ngTemplateOutlet]="itemAction" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                            <ng-container [ngTemplateOutlet]="itemActionList" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                            @if (itemInfo.media && itemInfo.media.length > 0) {
                              <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="MEDIA"></app-template-media>
                            }
                          </div>
                        </div>
                      }
                      <!-- Number Field -->
                      @if (itemInfo.fieldType === templateFieldType.Number && checkExpression(itemInfo, sectionInfo)) {
                        <div class="w-full xl:px-4 md:px-2 xl:py-1 pt-2 item-block">
                          <div class="static-label-mat-form-field">
                            <h4 class="text-xs text-left mb-1 font-sans font-medium text-[#474554]">
                              {{ itemInfo.caption }} @if (itemInfo?.isMandatory) {
                              <span class="text-red">*</span>
                            }
                          </h4>
                          <mat-form-field appearance="outline" class="w-full cmn-sm-form-field">
                            @if (itemInfo.helpText) {
                              <mat-icon matTooltipClass="cus-primary-tooltip"
                                matTooltip="{{ itemInfo.helpText }}" class="text-primary"
                              matSuffix svgIcon="c:question-circle"></mat-icon>
                            }
                            <input type="number" matInput [formControlName]="itemInfo.formattedItemCode" [(ngModel)]="checklists[itemInfo.formattedItemCode].val" placeholder="Enter Numeric">
                            @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.touched && templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('required')) {
                              <mat-error class="text-xs"
                                >
                                This field is required
                              </mat-error>
                            }
                          </mat-form-field>
                          <ng-container [ngTemplateOutlet]="itemAction" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                          <ng-container [ngTemplateOutlet]="itemActionList" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                          @if (itemInfo.media && itemInfo.media.length > 0) {
                            <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="MEDIA"></app-template-media>
                          }
                        </div>
                      </div>
                    }
                    <!-- TextArea Field -->
                    @if (itemInfo.fieldType === templateFieldType.TextArea && checkExpression(itemInfo, sectionInfo)) {
                      <div class="w-full xl:px-4 md:px-2 xl:py-1 pt-2 item-block">
                        <div class="static-label-mat-form-field">
                          <h4 class="text-xs text-left mb-1 font-sans font-medium text-[#474554]">
                            {{ itemInfo.caption }} @if (itemInfo?.isMandatory) {
                            <span class="text-red">*</span>
                          }
                        </h4>
                        <mat-form-field appearance="outline" class="w-full cmn-sm-form-field !h-[80px]">
                          @if (itemInfo.helpText) {
                            <mat-icon matTooltipClass="cus-primary-tooltip"
                              matTooltip="{{ itemInfo.helpText }}" class="text-primary"
                            matSuffix svgIcon="c:question-circle"></mat-icon>
                          }
                          <textarea matInput class="!resize-none !text-xs" style="height: 100% !important;"
                            [formControlName]="itemInfo.formattedItemCode" [(ngModel)]="checklists[itemInfo.formattedItemCode].val"
                          placeholder="Enter your text here" rows="3"></textarea>
                          @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.touched && templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('required')) {
                            <mat-error class="text-xs"
                              >
                              This field is required
                            </mat-error>
                          }
                        </mat-form-field>
                        <ng-container [ngTemplateOutlet]="itemAction" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                        <ng-container [ngTemplateOutlet]="itemActionList" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                        @if (itemInfo.media && itemInfo.media.length > 0) {
                          <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="MEDIA"></app-template-media>
                        }
                      </div>
                    </div>
                  }
                  <!-- Dropdown Field -->
                  @if (itemInfo.fieldType === templateFieldType.DropDown && checkExpression(itemInfo, sectionInfo)) {
                    <div class="w-full xl:px-4 md:px-2 xl:py-1 pt-2 item-block">
                      <div class="static-label-mat-form-field">
                        <h4 class="text-xs text-left mb-1 font-sans font-medium text-[#474554]">
                          {{ itemInfo.caption }} @if (itemInfo?.isMandatory) {
                          <span class="text-red">*</span>
                        }
                      </h4>
                      <mat-form-field appearance="outline" class="w-full cmn-sm-form-field">
                        @if (itemInfo.helpText) {
                          <mat-icon matTooltipClass="cus-primary-tooltip" matTooltip="{{ itemInfo.helpText }}" class="text-primary" matSuffix svgIcon="c:question-circle"></mat-icon>
                        }
                        <mat-select placeholder="Select"
                          [multiple]="itemInfo.allowMultiSelect"
                          [(ngModel)]="checklists[itemInfo.formattedItemCode].val"
                          [formControlName]="itemInfo.formattedItemCode"
                          (selectionChange)="calculateScore()">
                          <mat-option [value]="null" [disabled]="itemInfo.allowMultiSelect">Select</mat-option>
                          @if (itemInfo.displayResponseBy?.length > 0) {
                            @for (option of getFilteredOptions$(itemInfo) | async; track option) {
                              <mat-option [value]="option.val">
                                {{ option.val }}
                              </mat-option>
                            }
                          } @else {
                            @for (option of itemInfo.responseItems; track option) {
                              <mat-option [value]="option.val">
                                {{ option.val }}
                              </mat-option>
                            }
                          }
                        </mat-select>
                        @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.touched && templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('required')) {
                          <mat-error class="text-xs"
                            >
                            This field is required
                          </mat-error>
                        }
                      </mat-form-field>
                      <ng-container [ngTemplateOutlet]="itemAction" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                      <ng-container [ngTemplateOutlet]="itemActionList" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                      @if (itemInfo.media && itemInfo.media.length > 0) {
                        <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="MEDIA"></app-template-media>
                      }
                    </div>
                  </div>
                }
                <!-- Multiple Choice Field -->
                @if (itemInfo.fieldType === templateFieldType.MultipleChoice && checkExpression(itemInfo, sectionInfo)) {
                  <div class="w-full xl:px-4 md:px-2 xl:py-1 pt-2 item-block">
                    <div class="static-label-mat-form-field">
                      <h4 class="text-xs text-left font-sans font-medium text-[#474554] flex items-start">
                        <div>
                          {{ itemInfo.caption }} @if (itemInfo?.isMandatory) {
                          <span class="text-red">*</span>
                        }
                        @if (itemInfo.helpText) {
                          <mat-icon matTooltipClass="cus-primary-tooltip" matTooltip="{{ itemInfo.helpText }}" class="text-primary text-md !h-fit mt-[-7px]" svgIcon="c:question-circle"></mat-icon>
                        }
                      </div>
                    </h4>
                    <div class="w-full cmn-md-form-field">
                      <div class="mb-4">
                        <mat-chip-listbox class="mat-mdc-chip-set-stacked" [multiple]="itemInfo.allowMultiSelect"
                          [formControlName]="itemInfo.formattedItemCode"
                          [(ngModel)]="checklists[itemInfo?.formattedItemCode].val"
                          (ngModelChange)="calculateScore()">
                          @for (responseItem of itemInfo.responseItems; track responseItem) {
                            <mat-chip-option>{{responseItem.val}}</mat-chip-option>
                          }
                        </mat-chip-listbox>
                        @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.touched && templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('required')) {
                          <mat-error class="text-xs text-left"
                            >
                            This field is required
                          </mat-error>
                        }
                      </div>
                      <ng-container [ngTemplateOutlet]="itemAction" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                      <ng-container [ngTemplateOutlet]="itemActionList" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                      @if (itemInfo.media && itemInfo.media.length > 0) {
                        <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="MEDIA"></app-template-media>
                      }
                    </div>
                  </div>
                </div>
              }
              <!-- Lookup Field -->
              @if (itemInfo.fieldType === templateFieldType.LookUp && checkExpression(itemInfo, sectionInfo)) {
                <div class="w-full xl:px-4 md:px-2 xl:py-1 pt-2 item-block">
                  <div class="static-label-mat-form-field">
                    <h4 class="text-xs text-left mb-1 font-sans font-medium text-[#474554]">
                      {{ itemInfo.caption }} @if (itemInfo?.isMandatory) {
                      <span class="text-red">*</span>
                    }
                  </h4>
                  <mat-form-field appearance="outline" class="w-full cmn-sm-form-field cursor-pointer"
                    (click)="openLookupDialog(itemInfo)">
                    @if (itemInfo.helpText) {
                      <mat-icon matTooltipClass="cus-primary-tooltip" matTooltip="{{ itemInfo.helpText }}" class="text-primary" matSuffix svgIcon="c:question-circle"></mat-icon>
                    }
                    <input matInput readonly placeholder="Select" class="cursor-pointer"
                      [value]="getLookupDisplayValue(itemInfo)">
                    <mat-icon matSuffix class="text-gray-400" svgIcon="c:chevron-down"></mat-icon>
                  </mat-form-field>
                  <input type="hidden"
                    [(ngModel)]="checklists[itemInfo.formattedItemCode].val"
                    [formControlName]="itemInfo.formattedItemCode">
                  @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.touched && templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('required')) {
                    <mat-error class="text-xs text-left"
                      >
                      This field is required
                    </mat-error>
                  }
                  <ng-container [ngTemplateOutlet]="itemAction" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                  <ng-container [ngTemplateOutlet]="itemActionList" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                  @if (itemInfo.media && itemInfo.media.length > 0) {
                    <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="MEDIA"></app-template-media>
                  }
                </div>
              </div>
            }
            <!-- Checkbox Field -->
            @if (itemInfo.fieldType === templateFieldType.CheckBox && checkExpression(itemInfo, sectionInfo)) {
              <div class="w-full xl:px-4 md:px-2 xl:py-1 pt-2 text-left self-center item-block">
                <mat-checkbox class="w-full text-[#474554]" [(ngModel)]="checklists[itemInfo.formattedItemCode].val"
                  [formControlName]="itemInfo.formattedItemCode" color="primary">
                  {{ itemInfo.caption }}
                </mat-checkbox>
                @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.touched && templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('required')) {
                  <mat-error class="text-xs">
                    This field is required
                  </mat-error>
                }
                <ng-container [ngTemplateOutlet]="itemAction" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                <ng-container [ngTemplateOutlet]="itemActionList" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                @if (itemInfo.media && itemInfo.media.length > 0) {
                  <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="MEDIA"></app-template-media>
                }
              </div>
            }
            <!-- Currency Field -->
            @if (itemInfo.fieldType === templateFieldType.Currency && checkExpression(itemInfo, sectionInfo)) {
              <div class="w-full xl:px-4 md:px-2 xl:py-1 pt-2 item-block">
                <div class="static-label-mat-form-field">
                  <h4 class="text-xs text-left mb-1 font-sans font-medium text-[#474554]">
                    {{ itemInfo.caption }} @if (itemInfo?.isMandatory) {
                    <span class="text-red">*</span>
                  }
                </h4>
                <mat-form-field appearance="outline" class="w-full cmn-sm-form-field">
                  @if (itemInfo.helpText) {
                    <mat-icon matTooltipClass="cus-primary-tooltip"
                      matTooltip="{{ itemInfo.helpText }}" class="text-primary !pr-0"
                    matSuffix svgIcon="c:question-circle"></mat-icon>
                  }
                  <input matInput
                    [(ngModel)]="checklists[itemInfo.formattedItemCode].val"
                    [formControlName]="itemInfo.formattedItemCode" type="number"
                    [placeholder]="'Enter ' + itemInfo.caption" />
                    <mat-icon matSuffix class="!pl-1" svgIcon="c:dollar"></mat-icon>
                    @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.touched && templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('required')) {
                      <mat-error class="text-xs"
                        >
                        This field is required
                      </mat-error>
                    }
                  </mat-form-field>
                  <ng-container [ngTemplateOutlet]="itemAction" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                  <ng-container [ngTemplateOutlet]="itemActionList" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                  @if (itemInfo.media && itemInfo.media.length > 0) {
                    <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="MEDIA"></app-template-media>
                  }
                </div>
              </div>
            }
            <!-- Percentage Field -->
            @if (itemInfo.fieldType === templateFieldType.Percentage && checkExpression(itemInfo, sectionInfo)) {
              <div class="w-full xl:px-4 md:px-2 xl:py-1 pt-2 item-block">
                <div class="static-label-mat-form-field">
                  <h4 class="text-xs text-left mb-1 font-sans font-medium text-[#474554]">
                    {{ itemInfo.caption }} @if (itemInfo?.isMandatory) {
                    <span class="text-red">*</span>
                  }
                </h4>
                <mat-form-field appearance="outline" class="w-full cmn-sm-form-field">
                  <input matInput [(ngModel)]="checklists[itemInfo.formattedItemCode].val"
                    [formControlName]="itemInfo.formattedItemCode" type="number" min="0" max="100"
                    step="0.1" placeholder="Enter Percentage" />
                    <mat-icon matSuffix class="!pb-2">%</mat-icon>
                    @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('required')) {
                      <mat-error class="text-xs"
                        >
                        This field is required
                      </mat-error>
                    }
                  </mat-form-field>
                  <ng-container [ngTemplateOutlet]="itemAction" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                  <ng-container [ngTemplateOutlet]="itemActionList" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                  @if (itemInfo.media && itemInfo.media.length > 0) {
                    <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="MEDIA"></app-template-media>
                  }
                </div>
              </div>
            }
            <!-- Date Field -->
            @if (itemInfo.fieldType === templateFieldType.Date && checkExpression(itemInfo, sectionInfo)) {
              <div class="w-full xl:px-4 md:px-2 xl:py-1 pt-2 item-block">
                <div class="static-label-mat-form-field">
                  <h4 class="text-xs text-left mb-1 font-sans font-medium text-[#474554]">
                    {{ itemInfo.caption }} @if (itemInfo?.isMandatory) {
                    <span class="text-red">*</span>
                  }
                </h4>
                <mat-form-field appearance="outline" class="w-full cmn-sm-form-field">
                  @if (itemInfo.helpText) {
                    <mat-icon matTooltipClass="cus-primary-tooltip"
                      matTooltip="{{ itemInfo.helpText }}" class="text-primary !pr-0"
                    matSuffix svgIcon="c:question-circle"></mat-icon>
                  }
                  <input matInput
                    [matDatepicker]="datePicker"
                    [(ngModel)]="checklists[itemInfo.formattedItemCode].val"
                    [formControlName]="itemInfo.formattedItemCode" placeholder="Select Date">
                    <mat-datepicker-toggle matSuffix [for]="datePicker">
                      <mat-icon matDatepickerToggleIcon>
                        <img src="../../../../../assets/icons/calendar.svg" alt="" class="p-0.5">
                      </mat-icon>
                    </mat-datepicker-toggle>
                    <mat-datepicker #datePicker></mat-datepicker>
                    @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.touched && templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('required')) {
                      <mat-error class="text-xs"
                        >
                        This field is required
                      </mat-error>
                    }
                  </mat-form-field>
                  <ng-container [ngTemplateOutlet]="itemAction" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                  <ng-container [ngTemplateOutlet]="itemActionList" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                  @if (itemInfo.media && itemInfo.media.length > 0) {
                    <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="MEDIA"></app-template-media>
                  }
                </div>
              </div>
            }
            <!-- DateTime Field -->
            @if (itemInfo.fieldType === templateFieldType.DateTime && checkExpression(itemInfo, sectionInfo)) {
              <div class="w-full xl:px-4 md:px-2 xl:py-1 pt-2 item-block">
                <div class="static-label-mat-form-field">
                  <h4 class="text-xs text-left mb-1 font-sans font-medium text-[#474554]">
                    {{ itemInfo.caption }}
                    @if (itemInfo?.isMandatory) {
                      <span class="text-red">*</span>
                    }
                    <i class="fas fa-question-circle text-primary pl-2" data-toggle="tooltip" [attr.title]="itemInfo.helpText"></i>
                  </h4>
                  <div class="w-full mb-4">
                    <ejs-datetimepicker format="dd-MM-yyyy hh:mm a"
                      [strictMode]="true" [enableMask]="true"
                      [(ngModel)]="checklists[itemInfo.formattedItemCode].val"
                      [formControlName]="itemInfo.formattedItemCode"
                      [maskPlaceholder]="maskPlaceholder" placeholder="dd-mm-yyyy hh:mm tt"
                      class="w-full custom-dtp">
                    </ejs-datetimepicker>
                    @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.touched && templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('required')) {
                      <mat-error class="text-xs text-left"
                        >
                        This field is required
                      </mat-error>
                    }
                  </div>
                  <ng-container [ngTemplateOutlet]="itemAction" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                  <ng-container [ngTemplateOutlet]="itemActionList" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                  @if (itemInfo.media && itemInfo.media.length > 0) {
                    <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="MEDIA"></app-template-media>
                  }
                </div>
              </div>
            }
            <!-- Time Field -->
            @if (itemInfo.fieldType === templateFieldType.Time && checkExpression(itemInfo, sectionInfo)) {
              <div class="w-full xl:px-4 md:px-2 xl:py-1 pt-2 item-block">
                <div class="static-label-mat-form-field">
                  <h4 class="text-xs text-left mb-1 font-sans font-medium text-[#474554]">
                    {{ itemInfo.caption }} @if (itemInfo?.isMandatory) {
                    <span class="text-red">*</span>
                  }
                </h4>
                <mat-form-field appearance="outline" class="w-full cmn-sm-form-field">
                  @if (itemInfo.helpText) {
                    <mat-icon matTooltipClass="cus-primary-tooltip"
                      matTooltip="{{ itemInfo.helpText }}" class="text-primary"
                    matSuffix svgIcon="c:question-circle"></mat-icon>
                  }
                  <input matInput [matTimepicker]="timePicker" [(ngModel)]="checklists[itemInfo.formattedItemCode].val"
                    [formControlName]="itemInfo.formattedItemCode" placeholder="Select Time">
                    <mat-timepicker-toggle matIconSuffix [for]="timePicker"/>
                    <mat-timepicker #timePicker/>
                    @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.touched && templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('required')) {
                      <mat-error class="text-xs"
                        >
                        This field is required
                      </mat-error>
                    }
                  </mat-form-field>
                  <!-- Outside the field, so a saved record that already has it shows why before anything is touched. -->
                  @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('endBeforeStart')) {
                    <p class="text-xs text-left text-red mt-[-8px] mb-2" role="alert">The end time must be after the start time</p>
                  }
                  <ng-container [ngTemplateOutlet]="itemAction" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                  <ng-container [ngTemplateOutlet]="itemActionList" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                  @if (itemInfo.media && itemInfo.media.length > 0) {
                    <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="MEDIA"></app-template-media>
                  }
                </div>
              </div>
            }
            <!-- Email Field -->
            @if (itemInfo.fieldType === templateFieldType.Email && checkExpression(itemInfo, sectionInfo)) {
              <div class="w-full xl:px-4 md:px-2 xl:py-1 pt-2 item-block">
                <div class="static-label-mat-form-field">
                  <h4 class="text-xs text-left mb-1 font-sans font-medium text-[#474554]">
                    {{ itemInfo.caption }} @if (itemInfo?.isMandatory) {
                    <span class="text-red">*</span>
                  }
                </h4>
                <mat-form-field appearance="outline" class="w-full cmn-sm-form-field">
                  @if (itemInfo.helpText) {
                    <mat-icon matTooltipClass="cus-primary-tooltip"
                      matTooltip="{{ itemInfo.helpText }}" class="text-primary"
                    matSuffix svgIcon="c:question-circle"></mat-icon>
                  }
                  <input matInput type="email"
                    [(ngModel)]="checklists[itemInfo.formattedItemCode].val"
                    [formControlName]="itemInfo.formattedItemCode" placeholder="Enter Email">
                    @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.touched && templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('required')) {
                      <mat-error class="text-xs"
                        >
                        This field is required
                      </mat-error>
                    }
                    @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.touched && templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('email')) {
                      <mat-error class="text-xs"
                        >
                        Please enter a valid email address.
                      </mat-error>
                    }
                  </mat-form-field>
                  <ng-container [ngTemplateOutlet]="itemAction" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                  <ng-container [ngTemplateOutlet]="itemActionList" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                  @if (itemInfo.media && itemInfo.media.length > 0) {
                    <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="MEDIA"></app-template-media>
                  }
                </div>
              </div>
            }
            <!-- Phone Field -->
            @if (itemInfo.fieldType === templateFieldType.Phone && checkExpression(itemInfo, sectionInfo)) {
              <div class="w-full xl:px-4 md:px-2 xl:py-1 pt-2 item-block">
                <div class="static-label-mat-form-field">
                  <h4 class="text-xs text-left mb-1 font-sans font-medium text-[#474554]">
                    {{ itemInfo.caption }} @if (itemInfo?.isMandatory) {
                    <span class="text-red">*</span>
                  }
                </h4>
                <mat-form-field appearance="outline" class="w-full cmn-sm-form-field">
                  @if (itemInfo.helpText) {
                    <mat-icon matTooltipClass="cus-primary-tooltip"
                      matTooltip="{{ itemInfo.helpText }}" class="text-primary"
                    matSuffix svgIcon="c:question-circle"></mat-icon>
                  }
                  <input matInput type="tel"
                    [(ngModel)]="checklists[itemInfo.formattedItemCode].val"
                    [formControlName]="itemInfo.formattedItemCode" placeholder="Enter Phone no">
                    @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.touched && templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('required')) {
                      <mat-error class="text-xs"
                        >
                        This field is required
                      </mat-error>
                    }
                  </mat-form-field>
                  <ng-container [ngTemplateOutlet]="itemAction" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                  <ng-container [ngTemplateOutlet]="itemActionList" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                  @if (itemInfo.media && itemInfo.media.length > 0) {
                    <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="MEDIA"></app-template-media>
                  }
                </div>
              </div>
            }
            <!-- Address Field -->
            @if (itemInfo.fieldType === templateFieldType.Address && checkExpression(itemInfo, sectionInfo)) {
              <div class="w-full xl:px-4 md:px-2 xl:py-1 pt-2 item-block">
                <div class="static-label-mat-form-field">
                  <h4 class="text-xs text-left mb-1 font-sans font-medium text-[#474554]">
                    {{ itemInfo.caption }} @if (itemInfo?.isMandatory) {
                    <span class="text-red">*</span>
                  }
                </h4>
                <mat-form-field appearance="outline" class="w-full cmn-sm-form-field">
                  @if (itemInfo.helpText) {
                    <mat-icon matTooltipClass="cus-primary-tooltip"
                      matTooltip="{{ itemInfo.helpText }}" class="text-primary !pr-0"
                    matSuffix svgIcon="c:question-circle"></mat-icon>
                  }
                  <mat-icon class="text-primary !pr-2" matSuffix svgIcon="c:location"></mat-icon>
                  <input matInput
                    [formControlName]="itemInfo.formattedItemCode"
                    [(ngModel)]="checklists[itemInfo?.formattedItemCode].val"
                    googlePlacesAutocomplete (placeSelected)="fillAddressDetails($event, itemInfo.formattedItemCode)"
                    class="!text-xs" placeholder="Enter location"/>
                    @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.touched && templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('required')) {
                      <mat-error class="text-xs">
                        This field is required
                      </mat-error>
                    }
                  </mat-form-field>
                  <ng-container [ngTemplateOutlet]="itemAction" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                  <ng-container [ngTemplateOutlet]="itemActionList" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                  @if (itemInfo.media && itemInfo.media.length > 0) {
                    <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="MEDIA"></app-template-media>
                  }
                </div>
              </div>
            }
            <!-- Signature Field -->
            @if (itemInfo.fieldType === templateFieldType.Signature && checkExpression(itemInfo, sectionInfo)) {
              <div class="w-full xl:px-4 md:px-2 xl:py-1 pt-2 item-block">
                <div class="static-label-mat-form-field flex flex-col gap-3 mb-3">
                  <h4 class="text-xs text-left mb-1 font-sans font-medium text-[#474554]">
                    {{ itemInfo.caption }} @if (itemInfo?.isMandatory) {
                    <span class="text-red">*</span>
                  }
                </h4>
                @if ((checklists[itemInfo.formattedItemCode].val??'') !== '') {
                  <div class="mt-[-10px]">
                    <img src="{{ checklists[itemInfo.formattedItemCode].val }}" alt="" srcset="" />
                  </div>
                }
                @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('required')) {
                  <mat-error class="text-xs" class="!text-xs text-left">
                    This field is required
                  </mat-error>
                }
                @if (!readOnly) {
                  <button mat-raised-button (click)="openDrawingBoard(itemInfo)" class="!text-xs w-[180px] mt-[-10px]">Draw a Signature</button>
                }
                <ng-container [ngTemplateOutlet]="itemAction" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo, cssStyle: 'imgCommonAction' }"></ng-container>
                <ng-container [ngTemplateOutlet]="itemActionList" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
                @if (itemInfo.media && itemInfo.media.length > 0) {
                  <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="MEDIA"></app-template-media>
                }
              </div>
            </div>
          }
          <!-- Image Field -->
          @if (itemInfo.fieldType === templateFieldType.Image && checkExpression(itemInfo, sectionInfo)) {
            <div class="w-full xl:px-4 md:px-2 xl:py-1 pt-2 item-block">
              <div class="static-label-mat-form-field flex flex-col gap-3">
                <h4 class="text-xs text-left font-sans font-medium text-[#474554]">
                  {{ itemInfo.caption }} @if (itemInfo?.isMandatory) {
                  <span class="text-red">*</span>
                }
              </h4>
              <input type="file" accept=".jpeg, .jpg, .png, .bmp" #filePicker multiple (change)="selectImages($event, itemInfo, sectionInfo.sectionNo)" class="imgInput" style="display: none">
              <div class="flex flex-row flex-wrap gap-5 h-full mt-[-7px]">
                <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="ANS">
                </app-template-media>
              </div>
              @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('required')) {
                <mat-error class="text-xs" class="!text-xs text-left">
                  This field is required
                </mat-error>
              }
              <ng-container [ngTemplateOutlet]="itemAction" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo, cssStyle: 'imgCommonAction' }"></ng-container>
              <ng-container [ngTemplateOutlet]="itemActionList" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
              @if (itemInfo.media && itemInfo.media.length > 0) {
                <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="MEDIA"></app-template-media>
              }
            </div>
          </div>
        }
        <!-- Media Field (generic file upload — any file type, no image-only restriction) -->
        @if (itemInfo.fieldType === templateFieldType.Media && checkExpression(itemInfo, sectionInfo)) {
          <div class="w-full xl:px-4 md:px-2 xl:py-1 pt-2 item-block">
            <div class="static-label-mat-form-field flex flex-col gap-3">
              <h4 class="text-xs text-left font-sans font-medium text-[#474554]">
                {{ itemInfo.caption }} @if (itemInfo?.isMandatory) {
                <span class="text-red">*</span>
              }
            </h4>
              <div class="flex flex-row flex-wrap gap-5 h-full mt-[-7px]">
                <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="MEDIA_ANS">
                </app-template-media>
              </div>
              @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('required')) {
                <mat-error class="text-xs" class="!text-xs text-left">
                  This field is required
                </mat-error>
              }
              <ng-container [ngTemplateOutlet]="itemAction" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo, cssStyle: 'imgCommonAction' }"></ng-container>
              <ng-container [ngTemplateOutlet]="itemActionList" [ngTemplateOutletContext]="{ itemInfo: itemInfo, sectionNo: sectionInfo.sectionNo }"></ng-container>
              @if (itemInfo.media && itemInfo.media.length > 0) {
                <app-template-media [isDisabled]="readOnly" [itemInfo]="itemInfo" [checklists]="checklists" [recordId]="recordId" [moduleName]="templateType" [sectionNo]="sectionInfo.sectionNo" mediaSection="MEDIA"></app-template-media>
              }
            </div>
          </div>
        }
        <!-- Instruction Field -->
        @if (itemInfo.fieldType === templateFieldType.Instruction && checkExpression(itemInfo, sectionInfo)) {
          <div class="w-full xl:px-4 md:px-2 xl:py-1 pt-2 item-block">
            <div class="static-label-mat-form-field flex flex-col gap-3 mb-3 text-left">
              <h4 class="text-xs font-sans font-medium text-[#474554]">
                {{ itemInfo.caption }}
                @if (itemInfo.helpText) {
                  <mat-icon matTooltipClass="cus-primary-tooltip"
                    matTooltip="{{ itemInfo.helpText }}" class="text-primary text-xs"
                  matSuffix svgIcon="c:question-circle"></mat-icon>
                }
              </h4>
              @for (link of getLinks($safeNavigationMigration(itemInfo?.links), true); track link) {
                @if (!link.caption?.length) {
                  <div class="text-xs">
                    <a [href]="link.url" target="_blank">
                      <i class="text-gray fas fa-link"></i>
                      {{ link.url }}
                    </a>
                  </div>
                }
              }
              @for (link of getLinks($safeNavigationMigration(itemInfo?.links), false); track link) {
                @if (link.caption?.length > 0) {
                  <div class="text-xs">
                    <a [href]="link.url" target="_blank">
                      <img class="card-image w-20" [src]="getFileUrl(link.key, link.url)" alt="Media" />
                      {{ link.caption }}
                    </a>
                  </div>
                }
              }
            </div>
          </div>
        }
      }
    </div>
  </div>
</mat-expansion-panel>
}
@if (!readOnly && displayRepeatSectionButton(pageInfo, sectionInfo)) {
  <button mat-raised-button class="!text-xs mb-5 flex w-full" [class.preview-add-section]="collapsibleSections" (click)="addRepeatativeSection(pageInfo, sectionInfo)">
    <mat-icon class="pr-2 c-fa" svgIcon="c:plus"></mat-icon> {{ collapsibleSections ? 'Add another ' + sectionName(pageInfo, sectionInfo) : sectionInfo.caption }}
  </button>
}
}
<div class="flex gap-3 justify-center mb-1">
  @if (i > 0) {
    <button mat-raised-button matStepperPrevious>
      <div class="flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
        </svg>
        <span class="text-[13px]">Previous Page</span>
      </div>
    </button>
  }
  @if (i < (templateData.length-1)) {
    <button mat-raised-button matStepperNext>
      <div class="flex items-center">
        <span class="text-[13px]">Next Page</span>
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </button>
  }
  @if (isPreview && i === (templateData.length-1)) {
    <button mat-raised-button (click)="validatePage(pageInfo)">
      <span class="text-[13px]">Validate</span>
    </button>
  }
</div>
</mat-step>
}
}
</mat-stepper>
</form>
}

<ng-template #itemAction let-itemInfo="itemInfo" let-sectionNo="sectionNo" let-cssStyle="cssStyle" >
  @if (!isSystemForm && readOnly && (checklists[itemInfo.formattedItemCode].note ?? '') !== '') {
    <p class="text-xs text-left text-[#474554] mt-1"><i class="fas fa-comment-dots pr-2"></i>{{ checklists[itemInfo.formattedItemCode].note }}</p>
  }
  @if (!isSystemForm && !readOnly) {
    <div class="pt-1 !relative item-action mt-[-22px] float-right" [class]="cssStyle">
      <a class="!text-xs" tabindex="0" (click)="onClickNote(itemInfo)" (keydown.enter)="onClickNote(itemInfo)" [class]="(checklists[itemInfo.formattedItemCode].note??'') !== '' ? '!text-red' : ''">
        <i class="fas fa-comment-dots pr-2"></i>Note
      </a>
      <a class="!text-xs" tabindex="0" (click)="onClickMedia(itemInfo, sectionNo)" (keydown.enter)="onClickMedia(itemInfo, sectionNo)" [class]="checklists[itemInfo.formattedItemCode].media?.length > 0 ? '!text-red' : ''">
        <i class="fas fa-photo-video pr-2"></i>Media
      </a>
      <a class="!text-xs" tabindex="0" (click)="openActionPopup(itemInfo, null, sectionNo)" (keydown.enter)="openActionPopup(itemInfo, null, sectionNo)">
        <i class="fas fa-clipboard-check pr-2"></i>Action
      </a>
    </div>
  }
  @if (templatePreviewForm.get(itemInfo.formattedItemCode)?.touched && templatePreviewForm.get(itemInfo.formattedItemCode)?.hasError('custom')) {
    <mat-error class="text-xs"
      class="!text-xs text-left !flex flex-col"
      [innerHtml]="$safeNavigationMigration(templatePreviewForm.get(itemInfo.formattedItemCode)?.errors?.['custom']?.message)">
    </mat-error>
  }
</ng-template>

<ng-template #itemActionList let-itemInfo="itemInfo" let-sectionNo="sectionNo">
  @if (!isSystemForm && checklists[itemInfo.formattedItemCode].action?.length > 0) {
    <div class="w-full xl:px-4 md:px-2 xl:py-1 py-2 mb-3">
      <div class="flex justify-between gap-4 flex-wrap w-full items-center">
        <h4 class="text-xs font-sans font-medium">
          Action Items
        </h4>
      </div>
      @for (action of checklists[itemInfo.formattedItemCode]?.action; track action; let i = $index) {
        <div class="w-full py-1 px-1 flex justify-between text-xs text-[#474554] hover:bg-gray-100">
          <div class="flex items-center gap-2 flex-grow" [class.cursor-pointer]="!readOnly" role="button" tabindex="0" (click)="openActionPopup(itemInfo, action, sectionNo)" (keydown.enter)="openActionPopup(itemInfo, action, sectionNo)">
            <span class="px-2 py-[1px] text-xs font-medium text-gray-700 bg-gray-100 rounded-full border border-gray-200">
              A-{{action?.id}}
            </span>
            <span>{{ action.caption }}</span>
          </div>
          <div class="flex items-center gap-3">
            @switch (action.status) {
              @case (actionStatusID.ToDo) {
                <span class="px-2 py-[1px] text-xs font-medium text-yellow-800 bg-yellow-100 rounded-full border border-gray-200">
                  {{ actionStatus.ToDo }}
                </span>
              }
              @case (actionStatusID.InProgress) {
                <span class="px-2 py-[1px] text-xs font-medium text-slate-800 bg-slate-100 rounded-full border border-gray-200">
                  {{ actionStatus.InProgress }}
                </span>
              }
              @case (actionStatusID.Completed) {
                <span class="px-2 py-[1px] text-xs font-medium text-teal-800 bg-teal-100 rounded-full border border-gray-200">
                  {{ actionStatus.Completed }}
                </span>
              }
              @case (actionStatusID.Closed) {
                <span class="px-2 py-[1px] text-xs font-medium text-black-100 bg-gray-200 rounded-full border border-gray-200">
                  {{ actionStatus.Closed }}
                </span>
              }
            }
            @if (!readOnly) {
              <span class="cursor-pointer" title="Edit Action" role="button" tabindex="0" (click)="openActionPopup(itemInfo, action, sectionNo)" (keydown.enter)="openActionPopup(itemInfo, action, sectionNo)">
                <i class="far fa-edit text-primary"></i>
              </span>
              <span class="cursor-pointer" title="Delete Action" role="button" tabindex="0" (click)="onDeleteActionClick(itemInfo, action, sectionNo)" (keydown.enter)="onDeleteActionClick(itemInfo, action, sectionNo)">
                <mat-icon class="text-primary hover:text-red c-fa" svgIcon="c:trash"></mat-icon>
              </span>
            }
          </div>
        </div>
      }
    </div>
  }
</ng-template>
